/**
 * InvoicesModal - Modal de gestion des factures Stripe
 * 
 * Affiche toutes les factures avec statuts, montants et liens PDFs.
 * Permet de voir et télécharger les factures.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import StripeService from '../../services/api/stripeService';
import { formatLongDate, formatDateTime } from '../../utils/formatDate';

interface InvoicesModalProps {
    id: string;
    companyId: string;
    accessToken: string;
    onClose?: () => void;
}

const InvoicesModal: React.FC<InvoicesModalProps> = ({
    id,
    companyId,
    accessToken,
    onClose
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState<any[]>([]);
    const [total, setTotal] = useState(0);

    // Charger les factures depuis le backend
    useEffect(() => {
        const loadInvoices = async () => {
            setLoading(true);
            try {
                const response = await StripeService.getInvoices(companyId, 50, accessToken);
                console.log('[InvoicesModal] API Response:', response);
                if (response.success && response.data) {
                    console.log('[InvoicesModal] Invoices data:', response.data);
                    console.log('[InvoicesModal] Number of invoices:', response.data.invoices?.length);
                    console.log('[InvoicesModal] First invoice:', response.data.invoices?.[0]);
                    setInvoices(response.data.invoices || []);
                    setTotal(response.data.total || 0);
                } else {
                    console.error('[InvoicesModal] Failed to load invoices:', response);
                    setInvoices([]);
                }
            } catch (error) {
                console.error('[InvoicesModal] Error loading invoices:', error);
                setInvoices([]);
            } finally {
                setLoading(false);
            }
        };

        loadInvoices();
    }, [companyId, accessToken]);

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
        document.body.style.overflow = '';
    }, [onClose]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const handleModalHidden = () => {
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            document.body.style.overflow = 'hidden';

            // Ouvrir le modal automatiquement
            const bootstrap = (window as any).bootstrap;
            if (bootstrap) {
                const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
                modal.show();
            }

            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                document.body.style.overflow = '';
            };
        }
    }, [handleClose]);

    const getStatusBadgeClass = (status: string): string => {
        switch (status) {
            case 'paid': return 'bg-success';
            case 'open': return 'bg-warning text-dark';
            case 'uncollectible': return 'bg-danger';
            case 'void': return 'bg-secondary';
            default: return 'bg-secondary';
        }
    };

    const getStatusIcon = (status: string): string => {
        switch (status) {
            case 'paid': return 'fa-check-circle';
            case 'open': return 'fa-clock';
            case 'uncollectible': return 'fa-times-circle';
            case 'void': return 'fa-ban';
            default: return 'fa-file-invoice';
        }
    };

    // Utilisation de notre fonction utilitaire formatLongDate

    const formatAmount = (amount: number, currency: string): string => {
        console.log('[InvoicesModal] formatAmount called with:', { amount, currency, type: typeof amount });
        // Le backend envoie déjà le montant formaté (divisé par 100 pour devises normales)
        // Devises à zéro décimal : pas de décimales
        const zeroDecimalCurrencies = ['xaf', 'xof', 'jpy', 'krw', 'clp', 'vnd', 'bif', 'djf', 'gnf', 'kmf', 'pyg', 'rwf', 'ugx', 'vuv'];
        const isZeroDecimal = zeroDecimalCurrencies.includes(currency.toLowerCase());
        
        if (isZeroDecimal) {
            const formatted = `${Math.round(amount)} ${currency.toUpperCase()}`;
            console.log('[InvoicesModal] Zero decimal formatted:', formatted);
            return formatted;
        } else {
            const formatted = `${amount.toFixed(2)} ${currency.toUpperCase()}`;
            console.log('[InvoicesModal] Normal decimal formatted:', formatted);
            return formatted;
        }
    };

    const modalContent = (
        <div
            ref={modalRef}
            className="modal fade"
            data-bs-backdrop="true"
            data-bs-keyboard="true"
            id={id}
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable" style={{ zIndex: 10001, pointerEvents: 'auto' }}>
                <div className="modal-content" style={{ zIndex: 10002, pointerEvents: 'auto', borderRadius: '15px' }}>
                    <div className="modal-header" style={{ borderRadius: '15px 15px 0 0' }}>
                        <h5 className="modal-title text-theme" id={`${id}Label`}>
                            <i className="fa-solid fa-file-invoice-dollar me-2"></i>
                            {t('views.modal.invoices.title', 'Mes factures')}
                            {total > 0 && <span className="badge bg-primary ms-2">{total}</span>}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">{t('views.modal.invoices.loading', 'Chargement des factures...')}</p>
                            </div>
                        ) : invoices.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '15%' }}>
                                                <i className="fa-solid fa-calendar me-2"></i>
                                                {t('views.modal.invoices.date', 'Date')}
                                            </th>
                                            <th style={{ width: '20%' }}>
                                                <i className="fa-solid fa-file-invoice me-2"></i>
                                                {t('views.modal.invoices.number', 'Numéro')}
                                            </th>
                                            <th style={{ width: '15%' }}>
                                                <i className="fa-solid fa-dollar-sign me-2"></i>
                                                {t('views.modal.invoices.amount', 'Montant')}
                                            </th>
                                            <th style={{ width: '15%' }}>
                                                <i className="fa-solid fa-info-circle me-2"></i>
                                                {t('views.modal.invoices.status', 'Statut')}
                                            </th>
                                            <th style={{ width: '20%' }}>
                                                <i className="fa-solid fa-tag me-2"></i>
                                                {t('views.modal.invoices.description', 'Description')}
                                            </th>
                                            <th style={{ width: '15%' }} className="text-center">
                                                <i className="fa-solid fa-cog me-2"></i>
                                                {t('views.modal.invoices.actions', 'Actions')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((invoice: any) => (
                                            <tr key={invoice.id}>
                                                <td>{formatDateTime(invoice.created)}</td>
                                                <td>
                                                    <code className="small" style={{ opacity: 0.9 }}>
                                                        <i className={`fa-solid ${invoice.type === 'charge' ? 'fa-receipt' : invoice.type === 'payment_intent' ? 'fa-credit-card' : 'fa-file-invoice'} me-1`}></i>
                                                        {invoice.number || invoice.id.substring(0, 12)}
                                                    </code>
                                                </td>
                                                <td>
                                                    <strong className="text-success">{formatAmount(invoice.amount_paid, invoice.currency)}</strong>
                                                </td>
                                                <td>
                                                    <span className={`badge ${getStatusBadgeClass(invoice.status)} px-3 py-2`}>
                                                        <i className={`fa-solid ${getStatusIcon(invoice.status)} me-1`}></i>
                                                        {invoice.status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="small" style={{ opacity: 0.75 }}>
                                                        {invoice.description || 'Subscription payment'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="btn-group btn-group-sm" role="group">
                                                        {invoice.hosted_invoice_url && (
                                                            <a
                                                                href={invoice.hosted_invoice_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-outline-primary"
                                                                title={t('views.modal.invoices.view', 'Voir la facture')}
                                                            >
                                                                <i className="fa-solid fa-eye"></i>
                                                            </a>
                                                        )}
                                                        {invoice.invoice_pdf && (
                                                            <a
                                                                href={invoice.invoice_pdf}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-outline-danger"
                                                                title={t('views.modal.invoices.download', 'Télécharger PDF')}
                                                            >
                                                                <i className="fa-solid fa-file-pdf"></i>
                                                            </a>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <i className="fa-solid fa-file-invoice mb-3" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                                <p>{t('views.modal.invoices.empty', 'No invoices available')}</p>
                                <p className="small" style={{ opacity: 0.7 }}>
                                    {t('views.modal.invoices.emptyDescription', 
                                        'Your invoices will appear here after your first payments.')}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            <i className="fa-solid fa-times me-2"></i>
                            {t('words.close', 'Fermer')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default InvoicesModal;
